import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select } from "antd";
import ReportJewelryReportService from "../../../services/reports-Jewelry-service";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { PRICE_CONFIG } from "./_shared/jewelryReportConfigs";
import JewelryDetailSearchTable from "./_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "./_shared/jewelryHelpers";

const { Option } = Select;

// Parses a price-band label (e.g. "$1,001 - $2,500", "Under $100",
// "Over $10,001", "Call For Price") back into the {price, price1, price2}
// triple the table SP filters on. Ported as-is from the pre-redesign
// JewelryPriceReportMain's handleClick — proven parsing, only the chart
// event source changed (Chart.js slice -> band label string).
const parsePriceBand = (label) => {
  if (label === "Call For Price") {
    return { price: "Call For Price", price1: "", price2: "" };
  }
  const [low, high] = label.split("-");
  const lowPrice1 = parseInt(low.replace(/^.*?\$/, "").replace(/,/g, "").trim(), 10);
  const highPrice2 = parseInt((high || "").replace(/[$,]/g, ""), 10);
  // Backend's Price1/Price2 fields are typed string -- a bare number here
  // fails model binding (400) and the table never updates.
  return {
    price: "",
    price1: low.includes("Under") ? "0" : String(lowPrice1),
    price2: low.includes("Under") ? String(lowPrice1) : (highPrice2 ? String(highPrice2) : ""),
  };
};

const JewelryPriceReportMain = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMembershipType, setDealerMembershipType] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterPrice, setFilterPrice] = useState("");
  const [filterPrice1, setFilterPrice1] = useState("");
  const [filterPrice2, setFilterPrice2] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleCheckDealermembers(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckDealermembers = (id) => {
    ReportJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const memberType = loginDetials.loginDetials.responseData.dealerMemberTypeName;
        setDealerMembershipType(memberType);
        const fd = memberType === "Vendor" || memberType === "VendorDiamond" ? 1 : memberType === "Retailer" || memberType === "Combined" ? 2 : 3;
        setForDealer(fd);
        handleGetVendorDropdownData(id);
        handleGetRetailerDropdownData(id);
        handleLoadPriceData(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = (id) => {
    ReportJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = (id) => {
    ReportJewelryReportService.GetRetailerDropdownList({ dealerId: id, reporttype: "Dealer" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData(dealerId);
    } else {
      ReportJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData(dealerId);
    } else {
      ReportJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadPriceData = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setLoading(true);
    ReportJewelryReportService.GetClicksBasedOnPrice({
      dealerID: dealerIdArg.toString(),
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
      reporttype: "Dealer",
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData?.Table) {
          const rows = response.data.responseData.Table;
          setPriceData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.total || 0), 0));
        } else {
          setPriceData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setPriceData([]);
        setTotalClicks(0);
      });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      ReportJewelryReportService.GetClicksBasedOnPrice,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg.toString(),
        forDealer: forDealerArg ? forDealerArg.toString() : "",
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: extractDealerIds(rIds).toString(),
        vendorIds: extractDealerIds(vIds).toString(),
        reporttype: "Dealer",
      }),
      fromDate,
      toDate,
      "total"
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterPrice("");
    setFilterPrice1("");
    setFilterPrice2("");
    handleLoadPriceData(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = (label) => {
    const { price, price1, price2 } = parsePriceBand(label);
    setFilterPrice(price);
    setFilterPrice1(price1);
    setFilterPrice2(price2);
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(PRICE_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMembershipType !== "" && dealerMembershipType !== "Vendor" && dealerMembershipType !== "VendorDiamond" ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMembershipType !== "" && dealerMembershipType !== "Retailer" ? [`Retailers: ${retailerNames}`] : []),
          `Total Clicks: ${totalClicks}`,
        ],
        fileName: "Jewelry-Price-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const sortedPriceBands = [...priceData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
  const topBand = sortedPriceBands[0];
  const top3Sum = sortedPriceBands.slice(0, 3).reduce((sum, row) => sum + Number(row.total || 0), 0);
  const top3Pct = totalClicks > 0 ? Math.round((top3Sum / totalClicks) * 100) : 0;
  const topPct = totalClicks > 0 && topBand ? ((Number(topBand.total) / totalClicks) * 100).toFixed(1) : 0;

  return (
    <ReportScaffold
      config={PRICE_CONFIG}
      report={{
        [PRICE_CONFIG.dataKey]: priceData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onChartClick={handleSliceClick}
      insight={
        topBand && (
          <div className="edr-insight">
            💡 <b>{topBand.Range}</b> {t("is the price sweet spot at")} <b>{topPct}%</b> {t("of clicks")}, {t("and the top 3 bands capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("demand is tightly clustered around this range; stock and pricing should lean into it.")
              : t("demand is spread across a wider price range.")}
          </div>
        )
      }
      extraFilterFields={
        <>
          {/* Bug 1849: Vendor(s)/Retailer(s) filters removed from regular Jewelry Reports
          {dealerMembershipType !== "" && dealerMembershipType !== "Vendor" && dealerMembershipType !== "VendorDiamond" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Vendor(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                  {vendorList.map((item) => (
                    <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
          {dealerMembershipType !== "" && dealerMembershipType !== "Retailer" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Retailer(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                  {retailerList.map((item) => (
                    <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
          */}
        </>
      }
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={retailerIds}
          dealerMembershipType={dealerMembershipType}
          filterParamName={PRICE_CONFIG.detail.filterParamName}
          filterValue={filterPrice}
          price1={filterPrice1}
          price2={filterPrice2}
          fileNamePart={PRICE_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default JewelryPriceReportMain;
